import {
  formatCurrency,
  MICRO_CONVERSION,
  roundMicrosToNearestWholeCent,
} from '@/utils/money';
import {
  formatGrossProfitDisplay,
  formatPriceDisplayFromMicros,
} from '@/utils/price-editing';
import { isEqual } from 'lodash';
import { isValidNumber } from '@/utils/number';
import { PricingCalculations } from '@/utils/pricing-calculations';
import { SalesRepDisplayConfig } from '@domain/configuration/types';
import { SalesRepInfoCostMap } from '@/domain/pricing/types';
import { SalesRepOverrideMode, SalesRepPortionType } from '@/enums';
import { VariantPackCostType } from '@/types';

export enum PriceEditValidationError {
  EXCEEDS_CEILING = 'EXCEEDS_CEILING',
  EXCEEDS_FLOOR = 'EXCEEDS_FLOOR',
  GROSS_PROFIT_TOO_HIGH = 'GROSS_PROFIT_TOO_HIGH',
}

export enum PriceEditFieldType {
  GROSS_PROFIT = 'GROSS_PROFIT',
  PRICE = 'PRICE',
}

export interface PriceEditorConfig {
  /** Cost map keyed by cost type */
  costMap: SalesRepInfoCostMap;

  /** Currency code for display formatting */
  currencyCode: string | null | undefined;

  /** Currently saved temporary expiration date (YYYY-MM-DD). */
  currentExpirationDate: string | null;

  /** Current gross profit override as percentage */
  currentGrossProfitOverridePercent: number | null;

  /** Current price override in micros (per unit) */
  currentPriceOverrideInMicros: number | null;

  /** Customer cost type — used with costMap for cost resolution (DeviatedCost takes priority) */
  customerCostType: VariantPackCostType | null;

  /** Original per-unit price before any sales rep overrides (null if unavailable) */
  defaultUnitPriceInMicros: number | null;

  /** Portion type for display — UNIT or TOTAL */
  desiredPortionType: SalesRepPortionType | null;

  /** Whether override editing is disabled (merged from config + VP settings) */
  disableOverrideEditing: boolean;

  /** Whether sales rep info display is disabled */
  disableSalesRepInfo: boolean;

  /** Gross profit display configuration — accessed from SalesRepConfig */
  grossProfitDisplayConfig: SalesRepDisplayConfig | null;

  /** On-hand inventory quantity */
  onHandQuantity: number | null;

  /** Override mode — determines whether the rep edits by price or GP% */
  overrideMode: SalesRepOverrideMode;

  /** Whether peak price protection is enabled (merged by adapter from VP + relationship settings) */
  peakPriceProtection: boolean;

  /** Per-unit ceiling price in micros (null if no ceiling configured) */
  priceCeilingInMicros: number | null;

  /** Whether price floor is enabled */
  priceFloorEnabled: boolean;

  /** Current pack price in micros */
  priceInMicros: number | null;

  /** Whether GP% mode is enabled for this relationship */
  salesRepGrossProfitEnabled: boolean;

  /** Cost type used with costMap to resolve the floor price when priceFloorEnabled is true */
  salesRepPriceFloorMode: VariantPackCostType | null;

  /** Whether price mode is enabled for this relationship */
  salesRepPricingEnabled: boolean;

  /** Number of units per pack */
  unitCount: number | undefined;
}

export interface PriceEditorState {
  expirationDate: string | null;
  grossProfitDisplayText: string;
  isDirty: boolean;
  lastChangedField: PriceEditFieldType | null;
  priceDisplayText: string;
  salesRepGrossProfitPercent: number | null;
  salesRepPriceInMicros: number | null;
}

interface PriceEditorStateChangeListener {
  (state: PriceEditorState): void;
}

/**
 * PriceEditor manages the state and calculations for sales rep price editing.
 * It handles portion-based pricing (unit vs total), real-time calculations,
 * validation, and state management for price and gross profit overrides.
 */
export class PriceEditor {
  private config: PriceEditorConfig;

  private state: PriceEditorState;
  private listeners: Set<PriceEditorStateChangeListener> = new Set();
  private saveInProgress = false;

  constructor(config: PriceEditorConfig) {
    this.config = config;
    this.state = this.initializeState();
  }

  // ======================== STATE MANAGEMENT ========================

  /**
   * Initializes the editor state with current variant pack data.
   * Sets up display values for price and gross profit based on existing overrides.
   */
  private initializeState(): PriceEditorState {
    const initialPrice = this.getInitialSalesRepPriceInMicros();
    const initialGrossProfit = this.getInitialSalesRepGrossProfitPercent();

    // Display gross profit even without override (falls back to calculated value)
    const displayGrossProfit =
      initialGrossProfit ?? this.getCalculatedVariantPackGrossProfitPercent();

    return {
      expirationDate: this.config.currentExpirationDate,
      grossProfitDisplayText: formatGrossProfitDisplay(displayGrossProfit),
      isDirty: false,
      lastChangedField: null,
      priceDisplayText: this.formatPriceDisplay(initialPrice),
      salesRepGrossProfitPercent: initialGrossProfit,
      salesRepPriceInMicros: initialPrice,
    };
  }

  /**
   * Gets a readonly copy of the current editor state.
   */
  getState(): Readonly<PriceEditorState> {
    return { ...this.state };
  }

  /**
   * Checks if the current state has unsaved changes.
   * Compares current values against initial values from the database.
   */
  private calculateIsDirty(): boolean {
    const initialPrice = this.getInitialSalesRepPriceInMicros();
    const initialGrossProfit = this.getInitialSalesRepGrossProfitPercent();

    return (
      this.state.salesRepPriceInMicros !== initialPrice ||
      this.state.salesRepGrossProfitPercent !== initialGrossProfit
    );
  }

  /**
   * Stages an expiration date (YYYY-MM-DD) to be saved with the next commit.
   * Null stages "no temporary expiration"
   */
  setExpirationDate(date: string | null): void {
    if (this.saveInProgress) {
      return;
    }

    this.state.expirationDate = date;
    this.notifyListeners();
  }

  /**
   * Whether the pending expiration differs from the saved baseline.
   *
   * @returns True when the staged expiry differs from the saved expiry.
   */
  hasExpirationChange(): boolean {
    return this.state.expirationDate !== this.config.currentExpirationDate;
  }

  /**
   * Gets the saved temporary expiration date.
   *
   * @returns The saved expiry (YYYY-MM-DD), or null when the current override
   *   carries no sales-rep temporary expiry.
   */
  getSavedExpirationDate(): string | null {
    return this.config.currentExpirationDate;
  }

  /**
   * Marks the current state as saved, clearing the dirty flag.
   * Should be called after successful save operations.
   */
  markAsSaved(): void {
    this.state.isDirty = false;
    this.notifyListeners();
  }

  /**
   * Subscribes to state changes in the editor.
   * @param listener Function to call when state changes
   * @returns Unsubscribe function
   */
  subscribe(listener: PriceEditorStateChangeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notifies all subscribers of state changes.
   */
  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener(this.getState()));
  }

  /**
   * Sets the save-in-progress state to prevent conflicting updates.
   * @param inProgress Whether a save operation is currently in progress
   */
  /**
   * Returns whether a save operation is currently in progress.
   */
  isSaveInProgress(): boolean {
    return this.saveInProgress;
  }

  setSaveInProgress(inProgress: boolean): void {
    this.saveInProgress = inProgress;
    this.notifyListeners();
  }

  /**
   * Updates the editor with a new config and reinitializes state if needed.
   * Only reinitializes if there are no unsaved changes to preserve user edits.
   * Only notifies listeners if there are any state changes to avoid infinite render loops
   */
  updateConfig(config: PriceEditorConfig): void {
    this.config = config;

    // Only reinitialize if we don't have unsaved changes (preserve user edits).
    if (!this.state.isDirty && !this.hasExpirationChange()) {
      const oldState = this.getState();
      const newState = this.initializeState();

      // Only update and notify if state actually changed
      if (!isEqual(oldState, newState)) {
        this.state = newState;
        this.notifyListeners();
      }
    }
  }

  // ======================== USER INPUT HANDLING ========================

  /**
   * Converts a display value string (user input) to a price in micros.
   * @param displayValue User input string (e.g., "12.50")
   * @returns Numerical value or null if invalid
   */
  private parseDisplayValue(displayValue: string | null): number | null {
    if (!displayValue?.trim()) {
      return null;
    }
    const value = Number(displayValue);
    return !isValidNumber(value) ? null : value;
  }

  /**
   * Handles user input for price field updates.
   * Converts display price to unit price, calculates corresponding gross profit.
   * @param textValue Raw text input from price field
   */
  updatePrice(textValue: string | null): void {
    if (this.saveInProgress) {
      return;
    }

    const userInput = this.parseDisplayValue(textValue);

    let displayPriceInMicros = userInput ? userInput * MICRO_CONVERSION : null;
    let perUnitPriceInMicros =
      this.convertDisplayToUnitAmount(displayPriceInMicros);

    // Snap to floor price if it matches the user price when rounded to nearest cent
    // This prevents possible confusing validation errors when user enters the displayed floor price
    const floorPriceInMicros = this.snapToFloorPrice(
      displayPriceInMicros,
      perUnitPriceInMicros,
    );
    displayPriceInMicros = floorPriceInMicros.displayPrice;
    perUnitPriceInMicros = floorPriceInMicros.unitPrice;

    const grossProfitPercent =
      this.calculateGrossProfitPercentForDisplay(displayPriceInMicros);

    // Always preserve the user's input text to allow partial typing (like "0.", "-", etc.)
    // The final validation happens when they save
    this.state.priceDisplayText = textValue || '';
    this.state.salesRepPriceInMicros = perUnitPriceInMicros;
    this.state.salesRepGrossProfitPercent = grossProfitPercent;
    this.state.grossProfitDisplayText =
      formatGrossProfitDisplay(grossProfitPercent);
    this.state.isDirty = this.calculateIsDirty();
    this.state.lastChangedField = PriceEditFieldType.PRICE;

    this.notifyListeners();
  }

  /**
   * Handles user input for gross profit percentage field updates.
   * Calculates corresponding price based on gross profit and cost.
   * @param textValue Raw text input from gross profit field
   */
  updateGrossProfit(textValue: string | null): void {
    if (this.saveInProgress) {
      return;
    }

    const grossProfitPercent = this.parseDisplayValue(textValue);
    const displayPriceInMicros =
      this.calculateDisplayPriceFromGrossProfit(grossProfitPercent);
    const perUnitPriceInMicros =
      this.convertDisplayToUnitAmount(displayPriceInMicros);

    // Always preserve the user's input text to allow partial typing (like "0.", "-", etc.)
    // The final validation happens when they save
    this.state.grossProfitDisplayText = textValue || '';
    this.state.salesRepGrossProfitPercent = grossProfitPercent;
    this.state.salesRepPriceInMicros = perUnitPriceInMicros;
    this.state.priceDisplayText =
      formatPriceDisplayFromMicros(displayPriceInMicros);
    this.state.isDirty = this.calculateIsDirty();
    this.state.lastChangedField = PriceEditFieldType.GROSS_PROFIT;

    this.notifyListeners();
  }

  /**
   * Clears all price and gross profit edits, resetting to initial state.
   * Maintains calculated gross profit display even without overrides.
   */
  clearEdits(): void {
    if (this.saveInProgress) {
      return;
    }

    const initialPrice = this.getInitialSalesRepPriceInMicros();
    const initialGrossProfit = this.getInitialSalesRepGrossProfitPercent();

    this.state.expirationDate = this.config.currentExpirationDate;
    this.state.salesRepPriceInMicros = initialPrice;
    this.state.salesRepGrossProfitPercent = initialGrossProfit;
    this.state.priceDisplayText = this.formatPriceDisplay(initialPrice);

    // Show calculated gross profit even without override
    const displayGrossProfit =
      initialGrossProfit ?? this.getCalculatedVariantPackGrossProfitPercent();
    this.state.grossProfitDisplayText =
      formatGrossProfitDisplay(displayGrossProfit);
    this.state.isDirty = this.calculateIsDirty();
    this.state.lastChangedField = null;

    this.notifyListeners();
  }

  /**
   * Reverts price and GP to the original base values (before any overrides) and clears the staged expiry
   * Uses config.defaultUnitPriceInMicros rather than the saved override,
   * so "Revert" restores the original catalog price.
   */
  revertToDefaultPrice(): void {
    if (this.saveInProgress) {
      return;
    }

    const defaultPrice = this.config.defaultUnitPriceInMicros;
    const defaultGp = this.calculateGrossProfitPercent(defaultPrice);

    this.state.expirationDate = null;
    this.state.salesRepPriceInMicros = defaultPrice;
    this.state.priceDisplayText = this.formatPriceDisplay(defaultPrice);
    this.state.salesRepGrossProfitPercent = defaultGp;
    this.state.grossProfitDisplayText = formatGrossProfitDisplay(defaultGp);
    this.state.isDirty = this.calculateIsDirty();
    this.state.lastChangedField = null;

    this.notifyListeners();
  }

  /** Formatted default (pre-override) price for display in quick action detail. */
  getDefaultPriceDisplayText(): string {
    return this.formatPriceDisplay(this.config.defaultUnitPriceInMicros);
  }

  /** Formatted default (pre-override) GP% for display in quick action detail. */
  getDefaultGrossProfitDisplayText(): string {
    const defaultGp = this.calculateGrossProfitPercent(
      this.config.defaultUnitPriceInMicros,
    );
    return formatGrossProfitDisplay(defaultGp);
  }

  // ======================== DATA ACCESS ========================

  /**
   * Gets the gross profit percentage calculated using the variant pack's price.
   * Used as fallback when no override is set so that user can see the GP% of the variant pack.
   */
  private getCalculatedVariantPackGrossProfitPercent(): number | null {
    return this.calculateGrossProfitPercent(this.config.priceInMicros);
  }

  /**
   * Gets the display portion type from configuration.
   * Determines whether prices should be shown per unit or as total case pricing.
   */
  private getDisplayPortionType(): SalesRepPortionType {
    return this.config.desiredPortionType ?? SalesRepPortionType.UNIT;
  }

  /**
   * Gets the unit count for the variant pack. Defaults to 1.
   */
  private getUnitCount(): number {
    return this.config.unitCount ?? 1;
  }

  /**
   * Gets the customer's unit cost in micros based on a cost hierarchy.
   * Prioritizes adjusted (deviated) cost, then any specified cost type,
   * then defaults to the customer's configured cost type.
   * @param specifiedCostType Optional override for cost type.
   * @returns Per-unit cost in micros
   */
  private getCustomerUnitCostInMicros(
    specifiedCostType?: VariantPackCostType | null,
  ): number | null {
    const costMap = this.config.costMap;
    const costType = specifiedCostType ?? this.config.customerCostType;

    return (
      costMap[VariantPackCostType.DeviatedCost] ??
      (costType ? costMap[costType] : null) ??
      null
    );
  }

  /**
   * Gets the variant pack's sales rep per-unit price override in micros.
   */
  private getVariantPackSalesRepUnitPriceInMicros(): number | null {
    return this.config.currentPriceOverrideInMicros;
  }

  /**
   * Gets the variant pack's gross profit percentage override.
   */
  private getVariantPackGrossProfitOverride(): number | null {
    return this.config.currentGrossProfitOverridePercent;
  }

  /**
   * Gets the per-unit ceiling price limit in micros.
   */
  private getCeilingUnitPriceInMicros(): number | null {
    return this.config.priceCeilingInMicros;
  }

  /**
   * Gets the per-unit floor price limit in micros.
   */
  private getFloorUnitPriceInMicros(): number | null {
    if (this.config.priceFloorEnabled && this.config.salesRepPriceFloorMode) {
      return this.getCustomerUnitCostInMicros(
        this.config.salesRepPriceFloorMode,
      );
    }
    return null;
  }

  /**
   * Gets the on-hand inventory quantity for the variant pack.
   */
  getOnHandQuantity(): number | null {
    return this.config.onHandQuantity;
  }

  /**
   * Gets the initial sales rep price override (what's currently saved).
   * Returns per-unit price in micros.
   */
  private getInitialSalesRepPriceInMicros(): number | null {
    return this.getVariantPackSalesRepUnitPriceInMicros();
  }

  /**
   * Gets the initial gross profit percentage override (what's currently saved).
   * Handles peak price protection by calculating from price when applicable.
   */
  private getInitialSalesRepGrossProfitPercent(): number | null {
    if (this.isPeakPriceProtectionInEffect()) {
      return this.calculateGrossProfitPercent(
        this.getVariantPackSalesRepUnitPriceInMicros(),
      );
    }
    return this.getVariantPackGrossProfitOverride();
  }

  /**
   * Gets the cost to use for gross profit in micros calculations.
   * Distributors may specify a different cost type to use for this.
   * Falls back to the main customer cost if no display config is set.
   */
  private getGrossProfitDisplayUnitCostInMicros(): number | null {
    const grossProfitConfig = this.config.grossProfitDisplayConfig;
    if (grossProfitConfig?.cost_type) {
      return this.config.costMap[grossProfitConfig.cost_type] ?? null;
    }
    return this.getCustomerUnitCostInMicros();
  }

  /**
   * Checks if peak price protection is currently in effect. We can determine if peak price protection
   * is in effect if the variant pack's price override exceeds the gross profit override.
   */
  isPeakPriceProtectionInEffect(): boolean {
    if (
      !this.config.salesRepGrossProfitEnabled ||
      !this.config.peakPriceProtection
    ) {
      return false;
    }

    const salesRepPriceInMicros =
      this.getVariantPackSalesRepUnitPriceInMicros();
    const grossProfitOverride = this.getVariantPackGrossProfitOverride();
    const priceBasedOnGrossProfitOverride =
      this.calculateUnitPriceFromGrossProfit(grossProfitOverride);

    return Boolean(
      salesRepPriceInMicros &&
        priceBasedOnGrossProfitOverride &&
        roundMicrosToNearestWholeCent(salesRepPriceInMicros) >
          roundMicrosToNearestWholeCent(priceBasedOnGrossProfitOverride),
    );
  }

  // ======================== PERMISSIONS & FEATURE FLAGS ========================

  /**
   * Checks if price editing is enabled based on permissions, configuration and valid costs.
   * @param employeeCanWritePriceEditing Whether employee has write permissions
   * @returns True if price editing should be enabled
   */
  isPriceEditingEnabled(employeeCanWritePriceEditing: boolean): boolean {
    const userCanWrite =
      employeeCanWritePriceEditing && !this.config.disableOverrideEditing;
    const pricingModeEnabled =
      this.config.salesRepGrossProfitEnabled ||
      this.config.salesRepPricingEnabled;
    const hasValidCost = isValidNumber(this.getCustomerUnitCostInMicros());
    return Boolean(userCanWrite && pricingModeEnabled && hasValidCost);
  }

  /**
   * Checks if sales rep info display is disabled for this variant pack.
   */
  isSalesRepInfoDisabled(): boolean {
    return this.config.disableSalesRepInfo;
  }

  /**
   * Checks if prices are displayed per unit (as opposed to total pack price).
   */
  isPricingPerUnit(): boolean {
    return this.getDisplayPortionType() === SalesRepPortionType.UNIT;
  }

  /**
   * Currency code the editor's prices are denominated in.
   */
  getCurrencyCode(): string | undefined {
    return this.config.currencyCode ?? undefined;
  }

  // ======================== UNIT CONVERSION ========================

  /**
   * Converts a per-unit amount to display format based on portion type.
   * For TOTAL portion type, multiplies by unit count.
   * For UNIT portion type, returns an unchanged amount.
   * @param unitAmountInMicros Amount per unit in micros
   * @param portionTypeOverride Override for the portion type
   * @returns Display amount in micros (unit or total based on config)
   */
  private convertUnitAmountToDisplay(
    unitAmountInMicros?: number | null,
    portionTypeOverride?: SalesRepPortionType | null,
  ): number | null {
    if (!isValidNumber(unitAmountInMicros)) {
      return null;
    }

    const portionType = portionTypeOverride ?? this.getDisplayPortionType();
    if (portionType === SalesRepPortionType.TOTAL) {
      return unitAmountInMicros * this.getUnitCount();
    }
    return unitAmountInMicros;
  }

  /**
   * Converts a display amount back to per-unit amount.
   * For TOTAL portion type, divides by unit count.
   * For UNIT portion type, returns an unchanged amount.
   *
   * @param displayAmountInMicros Display amount in micros
   * @returns Per-unit amount in micros
   */
  private convertDisplayToUnitAmount(
    displayAmountInMicros?: number | null,
  ): number | null {
    if (!isValidNumber(displayAmountInMicros)) {
      return null;
    }

    const portionType = this.getDisplayPortionType();
    if (portionType === SalesRepPortionType.TOTAL) {
      return displayAmountInMicros / this.getUnitCount();
    }
    return displayAmountInMicros;
  }

  // ======================== PRECISION MATCHING ========================

  /**
   * Snaps user price to floor price when they round to the same cent value.
   * This prevents validation errors when user enters the displayed floor price,
   * but the true floor price is slightly higher when looking at the full micros value.
   *
   * For example:
   * - Floor displays as "$2.54" (from high-precision cost like 2,544,333 micros)
   * - User enters "$2.54" (becomes 2,540,000 micros)
   * - Gets validation error "Price cannot be less than $2.54"
   *
   * When rounded cent values match, snap to the exact floor precision
   * so user gets their intended price without validation errors.
   *
   * @param userDisplayPrice User's entered price in display micros
   * @param userUnitPrice User's price converted to unit micros
   * @returns Adjusted prices if floor match detected, otherwise original prices
   */
  private snapToFloorPrice(
    userDisplayPrice: number | null,
    userUnitPrice: number | null,
  ): { displayPrice: number | null; unitPrice: number | null } {
    if (!isValidNumber(userDisplayPrice) || !isValidNumber(userUnitPrice)) {
      return { displayPrice: userDisplayPrice, unitPrice: userUnitPrice };
    }

    const floorUnitPrice = this.getFloorUnitPriceInMicros();
    const floorDisplayPrice = this.convertUnitAmountToDisplay(floorUnitPrice);

    if (!floorUnitPrice || !isValidNumber(floorDisplayPrice)) {
      return { displayPrice: userDisplayPrice, unitPrice: userUnitPrice };
    }

    // Check if user entered a price that rounds to the same cent value as floor
    const userPriceRounded = roundMicrosToNearestWholeCent(userDisplayPrice);
    const floorPriceRounded = roundMicrosToNearestWholeCent(floorDisplayPrice);

    if (userPriceRounded === floorPriceRounded) {
      // User intended to match the floor price, so use exact un-rounded floor cost
      return {
        displayPrice: floorDisplayPrice,
        unitPrice: floorUnitPrice,
      };
    }

    // No match, return original values
    return { displayPrice: userDisplayPrice, unitPrice: userUnitPrice };
  }

  // ======================== CALCULATIONS ========================

  /**
   * Calculates gross profit percentage using display values (portion-adjusted).
   * Ensures both price and cost are in the same portion format for accurate calculation.
   * @param displayPriceInMicros Price in display format (unit or total)
   * @returns Gross profit percentage
   */
  private calculateGrossProfitPercentForDisplay(
    displayPriceInMicros: number | null,
  ): number | null {
    const perUnitCostInMicros = this.getCustomerUnitCostInMicros();
    const displayCostInMicros =
      this.convertUnitAmountToDisplay(perUnitCostInMicros);

    return PricingCalculations.grossProfitPercentFromPrice(
      displayPriceInMicros,
      displayCostInMicros,
    );
  }

  /**
   * Calculates display price from gross profit percentage.
   * Uses portion-adjusted cost for accurate calculation.
   * @param grossProfitPercent Target gross profit percentage
   * @returns Price in display format (unit or total), or null if GP% >= 100%
   */
  private calculateDisplayPriceFromGrossProfit(
    grossProfitPercent: number | null,
  ): number | null {
    // In gross profit mode, setting the value to 0 will delete the price edit in the backend
    // We should indicate this by setting the calculated price to null.
    if (
      this.config.overrideMode === SalesRepOverrideMode.GROSS_PROFIT &&
      grossProfitPercent === 0
    ) {
      return null;
    }

    const perUnitCostInMicros = this.getCustomerUnitCostInMicros();
    const displayCostInMicros =
      this.convertUnitAmountToDisplay(perUnitCostInMicros);

    return PricingCalculations.priceFromGrossProfitPercent(
      grossProfitPercent,
      displayCostInMicros,
    );
  }

  /**
   * Calculates gross profit percentage using per-unit values.
   * Used for base calculations without portion considerations.
   * @param priceInMicros Per-unit price in micros
   * @returns Gross profit percentage
   */
  private calculateGrossProfitPercent(
    priceInMicros?: number | null,
  ): number | null {
    return PricingCalculations.grossProfitPercentFromPrice(
      priceInMicros,
      this.getCustomerUnitCostInMicros(),
    );
  }

  /**
   * Calculates per-unit price from gross profit percentage.
   * Uses per-unit cost for calculation, returns per-unit price.
   * @param grossProfitPercent Target gross profit percentage
   * @returns Per-unit price in micros
   */
  private calculateUnitPriceFromGrossProfit(
    grossProfitPercent: number | null,
  ): number | null {
    return PricingCalculations.priceFromGrossProfitPercent(
      grossProfitPercent,
      this.getCustomerUnitCostInMicros(),
    );
  }

  /**
   * Gets the current price in micros for display purposes.
   * Uses the editor state's price (or falls back to initial) and converts to display format.
   * @returns Current price in display format (unit or total based on config)
   */
  getCurrentPriceInMicros(): number | null {
    const priceInMicros =
      this.state.salesRepPriceInMicros ??
      this.getInitialSalesRepPriceInMicros();
    return this.convertUnitAmountToDisplay(priceInMicros);
  }

  /**
   * Gets the base variant pack price in display format (unit or total based on config).
   * Unlike getCurrentPriceInMicros which returns the override price, this always
   * returns the underlying variant pack unit price — useful as a display fallback
   * when no override is set.
   */
  getBasePriceInMicros(): number | null {
    return this.convertUnitAmountToDisplay(this.config.priceInMicros);
  }

  /**
   * Gets the total pack price in micros (unit price × unit count), regardless of
   * the configured portion type.
   */
  getPackPriceInMicros(): number | null {
    const priceInMicros =
      this.state.salesRepPriceInMicros ??
      this.getInitialSalesRepPriceInMicros();
    return this.convertUnitAmountToDisplay(
      priceInMicros,
      SalesRepPortionType.TOTAL,
    );
  }

  /**
   * GP% for the effective price (current edit, else saved override). A cost/price
   * ratio, so independent of portion type; null when no override is set.
   */
  getGrossProfitPercent(): number | null {
    const priceInMicros =
      this.state.salesRepPriceInMicros ??
      this.getInitialSalesRepPriceInMicros();
    return this.calculateGrossProfitPercent(priceInMicros);
  }

  /**
   * Calculates gross profit amount in micros (profit = price - cost).
   * Uses portion-adjusted values for display purposes.
   * @returns Gross profit amount in display format
   */
  getGrossProfitInMicros(): number | null {
    const priceInMicros =
      this.state.salesRepPriceInMicros ??
      this.getInitialSalesRepPriceInMicros() ??
      this.config.priceInMicros;
    const costInMicros = this.getGrossProfitDisplayUnitCostInMicros();
    const grossProfitConfig = this.config.grossProfitDisplayConfig;

    // Allow overriding the portion type via the sales rep display config
    const portionTypeOverride = grossProfitConfig?.portion_type ?? undefined;
    const displayCost = this.convertUnitAmountToDisplay(
      costInMicros,
      portionTypeOverride,
    );
    const displayPrice = this.convertUnitAmountToDisplay(
      priceInMicros,
      portionTypeOverride,
    );

    if (!isValidNumber(displayPrice) || !isValidNumber(displayCost)) {
      return null;
    }

    const calculatedValue = displayPrice - displayCost;

    // Handle negative values based on configuration
    if (
      calculatedValue < 0 &&
      grossProfitConfig?.display_negative_value === false
    ) {
      return 0;
    }
    return calculatedValue;
  }

  // ======================== FORMATTING & VALIDATION ========================

  /**
   * Formats a per-unit price for display in the appropriate portion format.
   * @param perUnitPriceInMicros Per-unit price in micros
   * @returns Formatted price string for display
   */
  private formatPriceDisplay(perUnitPriceInMicros: number | null): string {
    const displayPriceInMicros =
      this.convertUnitAmountToDisplay(perUnitPriceInMicros);
    return formatPriceDisplayFromMicros(displayPriceInMicros);
  }

  /**
   * Formats an amount in micros as currency.
   * @param amountInMicros Amount in micros
   * @returns Formatted currency string
   */
  private formatCurrency(amountInMicros: number): string {
    return formatCurrency(amountInMicros, this.config.currencyCode);
  }

  /**
   * Validates the current per-unit price against floor and ceiling limits.
   * Also validates that gross profit percentage is less than 100%.
   * Values are adjusted to the desired portion type before validation.
   * Returns validation result with portion-adjusted error messages.
   * @returns Validation result with error details if invalid
   */
  validate(editorState: PriceEditorState): {
    bound?: string;
    reason?: PriceEditValidationError;
    valid: boolean;
  } {
    const perUnitPriceInMicros = editorState.salesRepPriceInMicros;
    const grossProfitPercent = editorState.salesRepGrossProfitPercent;
    const floorUnitPriceInMicros = this.getFloorUnitPriceInMicros();
    const ceilingUnitPriceInMicros = this.getCeilingUnitPriceInMicros();

    // Validate that GP% is less than 100%
    if (isValidNumber(grossProfitPercent) && grossProfitPercent >= 100) {
      return {
        reason: PriceEditValidationError.GROSS_PROFIT_TOO_HIGH,
        valid: false,
      };
    }

    const displayPriceInMicros =
      this.convertUnitAmountToDisplay(perUnitPriceInMicros);
    const displayFloorPrice = this.convertUnitAmountToDisplay(
      floorUnitPriceInMicros,
    );
    const displayCeilingPrice = this.convertUnitAmountToDisplay(
      ceilingUnitPriceInMicros,
    );

    if (displayPriceInMicros === null) {
      return { valid: true };
    }

    if (displayFloorPrice && displayPriceInMicros! < displayFloorPrice) {
      return {
        bound: this.formatCurrency(displayFloorPrice),
        reason: PriceEditValidationError.EXCEEDS_FLOOR,
        valid: false,
      };
    }
    if (displayCeilingPrice && displayPriceInMicros! > displayCeilingPrice) {
      return {
        bound: this.formatCurrency(displayCeilingPrice),
        reason: PriceEditValidationError.EXCEEDS_CEILING,
        valid: false,
      };
    }
    return { valid: true };
  }
}
